/**
 * Spaces State Helpers
 *
 * Shared client-side helpers for keeping Spaces navigation and list state
 * consistent across the sidebar and `/spaces` pages.
 *
 * Primary responsibilities:
 * - Keep active spaces sorted by pin and newest-first creation order
 * - Normalize nested item URLs to one query contract
 *
 * Dependencies:
 * - Supabase-generated `spaces` table type
 * - Shared Spaces selection types
 */

import { Tables } from "@/supabase/types"
import type {
  SpaceLinkedItemType,
  SpaceSelectableItemType
} from "@/types/spaces"

type SpaceRecord = Tables<"spaces">

type SearchParamsLike = {
  get: (key: string) => string | null
}

type SpaceItemTarget = {
  type: SpaceLinkedItemType
  id: string
}

type SpaceItemHrefTarget = SpaceItemTarget & {
  spaceId: string
}

type SpaceItemHrefOptions = {
  params?: Record<string, string | null | undefined>
}

type HrefParts = {
  pathname: string
  search: string
}

const LEGACY_ITEM_KEYS: SpaceSelectableItemType[] = [
  "conversation",
  "sprint",
  "task",
  "note",
  "folder",
  "sheet"
]

const getTimestamp = (value: string | null) => {
  const timestamp = value ? Date.parse(value) : Number.NEGATIVE_INFINITY
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY
}

const compareTimestampDesc = (left: string | null, right: string | null) => {
  const leftTimestamp = getTimestamp(left)
  const rightTimestamp = getTimestamp(right)

  if (leftTimestamp === rightTimestamp) {
    return 0
  }

  return rightTimestamp - leftTimestamp
}

export const sortSpaces = (spaces: SpaceRecord[]) => {
  return [...spaces].sort((left, right) => {
    const pinnedDiff =
      Number(Boolean(right.favorited)) - Number(Boolean(left.favorited))

    if (pinnedDiff !== 0) {
      return pinnedDiff
    }

    const createdAtDiff = compareTimestampDesc(left.created_at, right.created_at)

    if (createdAtDiff !== 0) {
      return createdAtDiff
    }

    const nameDiff = left.name.localeCompare(right.name)

    if (nameDiff !== 0) {
      return nameDiff
    }

    return left.id.localeCompare(right.id)
  })
}

export const upsertSpace = (
  spaces: SpaceRecord[],
  nextSpace: SpaceRecord
) => {
  return sortSpaces([
    nextSpace,
    ...spaces.filter((space) => space.id !== nextSpace.id)
  ])
}

export const upsertSpaceIntoKnownList = (
  spaces: SpaceRecord[] | undefined,
  nextSpace: SpaceRecord
) => {
  if (!spaces || spaces.length === 0) {
    return spaces
  }

  return upsertSpace(spaces, nextSpace)
}

export const removeSpaceFromList = (
  spaces: SpaceRecord[],
  spaceId: string
) => {
  return spaces.filter((space) => space.id !== spaceId)
}

/**
 * Bare Space shell path (`/spaces/:id`) — Workspace Pulse chrome /
 * radar / spotlight only. Not a Crew Go target (pulse-card-target
 * fail-closes bare spaceId). Prefer this over `/spaces/${id}` forks.
 */
export function buildSpaceShellHref(spaceId: string): string {
  const id = typeof spaceId === "string" ? spaceId.trim() : ""
  return id ? `/spaces/${id}` : "/spaces"
}

export const buildSpaceItemHref = (
  spaceId: string,
  item: SpaceItemTarget,
  options?: SpaceItemHrefOptions
) => {
  if (item.type === "design") {
    return `/spaces/${spaceId}/designs/${item.id}`
  }

  if (item.type === "whiteboard") {
    return `/spaces/${spaceId}/whiteboards/${item.id}`
  }

  const params = new URLSearchParams({
    itemType: item.type,
    itemId: item.id
  })
  for (const [key, value] of Object.entries(options?.params ?? {})) {
    const normalizedValue = value?.trim()

    if (normalizedValue) {
      params.set(key, normalizedValue)
    }
  }

  return `/spaces/${spaceId}?${params.toString()}`
}

export const buildStandaloneSpaceItemHref = (
  item: Pick<SpaceItemTarget, "type" | "id">
) => {
  return `/items/${item.type}/${item.id}`
}

export const isSpaceSelectableItemType = (
  value: string | null
): value is SpaceSelectableItemType => {
  return LEGACY_ITEM_KEYS.includes(value as SpaceSelectableItemType)
}

export const getSpaceItemFromSearchParams = (
  searchParams: SearchParamsLike | null
): SpaceItemTarget | null => {
  const itemType = searchParams?.get("itemType")
  const itemId = searchParams?.get("itemId")

  if (isSpaceSelectableItemType(itemType) && itemId) {
    return {
      type: itemType,
      id: itemId
    }
  }

  for (const legacyType of LEGACY_ITEM_KEYS) {
    const legacyId = searchParams?.get(legacyType)

    if (legacyId) {
      return {
        type: legacyType,
        id: legacyId
      }
    }
  }

  return null
}

const getHrefParts = (href: string | null | undefined): HrefParts | null => {
  const trimmedHref = href?.trim()

  if (!trimmedHref) {
    return null
  }

  if (trimmedHref.startsWith("/")) {
    const [withoutHash] = trimmedHref.split("#")
    const queryIndex = withoutHash.indexOf("?")

    return {
      pathname:
        queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex),
      search: queryIndex === -1 ? "" : withoutHash.slice(queryIndex + 1)
    }
  }

  try {
    const url = new URL(trimmedHref)
    return {
      pathname: url.pathname,
      search: url.search.slice(1)
    }
  } catch {
    return null
  }
}

export const getSpaceItemFromHref = (
  href: string | null | undefined
): SpaceItemHrefTarget | null => {
  const hrefParts = getHrefParts(href)

  if (!hrefParts) {
    return null
  }

  const path = hrefParts.pathname.replace(/\/+$/, "")
  const designMatch = path.match(/^\/spaces\/([^/]+)\/designs\/([^/]+)$/)

  if (designMatch?.[1] && designMatch?.[2]) {
    return {
      spaceId: designMatch[1],
      type: "design",
      id: designMatch[2]
    }
  }

  const whiteboardMatch = path.match(
    /^\/spaces\/([^/]+)\/whiteboards\/([^/]+)$/
  )

  if (whiteboardMatch?.[1] && whiteboardMatch?.[2]) {
    return {
      spaceId: whiteboardMatch[1],
      type: "whiteboard",
      id: whiteboardMatch[2]
    }
  }

  const match = path.match(/^\/spaces\/([^/]+)$/)

  if (!match?.[1]) {
    return null
  }

  const item = getSpaceItemFromSearchParams(
    new URLSearchParams(hrefParts.search)
  )

  return item
    ? {
        ...item,
        spaceId: match[1]
      }
    : null
}
